/**
 * 360° Sprite Viewer Component
 * 
 * A custom web component that enables drag-to-rotate interaction on sprite sheets,
 * providing smooth 360° product viewing without video seeking issues.
 * 
 * Features:
 * - Drag to rotate through frames (horizontal gesture)
 * - Instant frame switching (no seeking delays)
 * - Auto-rotate animation on first load
 * - Visual hint overlay to indicate interactivity
 * - Touch and pointer event support
 * - Seamless looping in both directions
 * 
 * Usage:
 * <sprite-360-viewer data-frames="36" data-columns="6" data-autorotate="true">
 *   <img src="sprite-sheet.webp" alt="Product 360 sprite 36 6">
 * </sprite-360-viewer>
 * 
 * Alt text format: "360 sprite [frames] [columns]" or just include "360 sprite"
 * for defaults (36 frames, 6 columns)
 */

if (!customElements.get('sprite-360-viewer')) {
  class Sprite360Viewer extends HTMLElement {
    constructor() {
      super();
      this.img = null;
      this.frames = 36;
      this.columns = 6;
      this.rows = 6;
      this.currentFrame = 0;
      this.frameWidth = 0;
      this.frameHeight = 0;
      this.isDragging = false;
      this.lastX = 0;
      this.dragAccumulator = 0;
      this.sensitivity = 15; // pixels of drag per frame change (higher = slower)
      this.touchSensitivity = 20; // touch sensitivity (higher = slower)
      this.hasInteracted = false;
      this.autorotateComplete = false;
      this.autorotateInterval = null;
      this.hintTimeout = null;
      
      // Bound methods
      this.onPointerDown = this.handlePointerDown.bind(this);
      this.onPointerMove = this.handlePointerMove.bind(this);
      this.onPointerUp = this.handlePointerUp.bind(this);
    }
    
    connectedCallback() {
      this.img = this.querySelector('img');
      if (!this.img) return;
      
      // Parse configuration from data attributes
      if (this.dataset.frames) this.frames = parseInt(this.dataset.frames);
      if (this.dataset.columns) this.columns = parseInt(this.dataset.columns);
      if (this.dataset.sensitivity) this.sensitivity = parseFloat(this.dataset.sensitivity);
      
      this.rows = Math.ceil(this.frames / this.columns);
      
      // Add component class for styling
      this.classList.add('sprite-360-viewer');
      
      // Create hint overlay
      this.createHintOverlay();
      
      // Wait for image to load to get dimensions
      if (this.img.complete && this.img.naturalWidth > 0) {
        this.onImageLoad();
      } else {
        this.img.addEventListener('load', () => this.onImageLoad(), { once: true });
      }
      
      // Bind pointer events
      this.addEventListener('pointerdown', this.onPointerDown);
      
      // Prevent context menu on long press (mobile)
      this.addEventListener('contextmenu', (e) => e.preventDefault());
    }
    
    disconnectedCallback() {
      this.removeEventListener('pointerdown', this.onPointerDown);
      document.removeEventListener('pointermove', this.onPointerMove);
      document.removeEventListener('pointerup', this.onPointerUp);
      
      if (this.autorotateInterval) {
        clearInterval(this.autorotateInterval);
      }
      if (this.hintTimeout) {
        clearTimeout(this.hintTimeout);
      }
    }
    
    onImageLoad() {
      // Calculate frame dimensions from sprite sheet
      this.frameWidth = this.img.naturalWidth / this.columns;
      this.frameHeight = this.img.naturalHeight / this.rows;
      
      // Setup the clipping container
      this.setupContainer();
      
      // Show first frame
      this.showFrame(0);
      
      // Show hint
      this.showHint();
      
      // Autorotate if enabled
      if (this.dataset.autorotate === 'true') {
        this.autorotate();
      }
    }
    
    setupContainer() {
      // Container clips to show only one frame
      this.style.setProperty('display', 'block', 'important');
      this.style.setProperty('position', 'relative', 'important');
      this.style.setProperty('width', '100%', 'important');
      this.style.setProperty('overflow', 'hidden', 'important');
      this.style.cursor = 'grab';
      this.style.userSelect = 'none';
      this.style.touchAction = 'pan-y';
      
      // Calculate container height from its width and frame aspect ratio
      const containerWidth = this.offsetWidth;
      const frameAspect = this.frameHeight / this.frameWidth;
      const containerHeight = containerWidth * frameAspect;
      this.style.setProperty('height', containerHeight + 'px', 'important');
      
      // Calculate image dimensions - it should be columns * containerWidth wide
      const imgDisplayWidth = containerWidth * this.columns;
      const imgDisplayHeight = imgDisplayWidth * (this.img.naturalHeight / this.img.naturalWidth);
      
      // Store these for showFrame calculations
      this.imgDisplayWidth = imgDisplayWidth;
      this.imgDisplayHeight = imgDisplayHeight;
      
      // Image is positioned absolutely with explicit pixel dimensions
      this.img.style.setProperty('display', 'block', 'important');
      this.img.style.setProperty('position', 'absolute', 'important');
      this.img.style.setProperty('top', '0', 'important');
      this.img.style.setProperty('left', '0', 'important');
      this.img.style.setProperty('width', imgDisplayWidth + 'px', 'important');
      this.img.style.setProperty('height', imgDisplayHeight + 'px', 'important');
      this.img.style.setProperty('max-width', 'none', 'important');
      this.img.style.setProperty('pointer-events', 'none', 'important');
      this.img.style.willChange = 'transform';
    }
    
    showFrame(index) {
      // Wrap around for seamless looping
      this.currentFrame = ((index % this.frames) + this.frames) % this.frames;
      
      // Calculate position in grid
      const col = this.currentFrame % this.columns;
      const row = Math.floor(this.currentFrame / this.columns);
      
      // Calculate pixel offset based on stored dimensions
      const frameW = this.imgDisplayWidth / this.columns;
      const frameH = this.imgDisplayHeight / this.rows;
      
      const xPx = col * frameW;
      const yPx = row * frameH;
      
      this.img.style.transform = `translate(-${xPx}px, -${yPx}px)`;
    }
    
    createHintOverlay() {
      const hint = document.createElement('div');
      hint.className = 'sprite-360-hint';
      hint.innerHTML = `
        <svg class="sprite-360-hint__icon" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="24" cy="24" r="22" stroke="currentColor" stroke-width="2" stroke-dasharray="4 4" opacity="0.5"/>
          <path d="M32 24C32 28.4183 28.4183 32 24 32C19.5817 32 16 28.4183 16 24C16 19.5817 19.5817 16 24 16" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/>
          <path d="M24 12L28 16L24 20" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M8 24H12M36 24H40" stroke="currentColor" stroke-width="2" stroke-linecap="round" opacity="0.5"/>
        </svg>
        <span class="sprite-360-hint__text">360°</span>
      `;
      this.hint = hint;
      this.appendChild(hint);
    }
    
    showHint() {
      if (this.hint && !this.hasInteracted) {
        this.hint.classList.add('sprite-360-hint--visible');
        
        // Auto-hide after 4 seconds
        this.hintTimeout = setTimeout(() => {
          this.hideHint();
        }, 4000);
      }
    }
    
    hideHint() {
      if (this.hint) {
        this.hint.classList.remove('sprite-360-hint--visible');
        this.hint.classList.add('sprite-360-hint--hidden');
      }
    }
    
    autorotate() {
      if (this.autorotateComplete || this.hasInteracted) return;
      
      let frame = 0;
      this.autorotateInterval = setInterval(() => {
        if (this.hasInteracted) {
          clearInterval(this.autorotateInterval);
          return;
        }
        
        frame++;
        this.showFrame(frame);
        
        // Stop after one full rotation
        if (frame >= this.frames) {
          clearInterval(this.autorotateInterval);
          this.autorotateComplete = true;
          this.showFrame(0);
        }
      }, 50); // ~20fps rotation speed
    }
    
    handlePointerDown(event) {
      if (event.button !== 0) return;
      
      // Stop autorotate
      if (this.autorotateInterval) {
        clearInterval(this.autorotateInterval);
        this.autorotateComplete = true;
      }
      
      this.isDragging = true;
      this.lastX = event.clientX;
      this.dragAccumulator = 0;
      
      // Hide hint on first interaction
      if (!this.hasInteracted) {
        this.hasInteracted = true;
        this.hideHint();
        if (this.hintTimeout) {
          clearTimeout(this.hintTimeout);
        }
      }
      
      // Visual feedback
      this.style.cursor = 'grabbing';
      
      // Capture pointer
      this.setPointerCapture(event.pointerId);
      
      // Listen for move/up
      document.addEventListener('pointermove', this.onPointerMove);
      document.addEventListener('pointerup', this.onPointerUp);
      
      event.preventDefault();
    }
    
    handlePointerMove(event) {
      if (!this.isDragging) return;
      
      const delta = event.clientX - this.lastX;
      this.lastX = event.clientX;
      
      // Accumulate drag distance
      const sens = event.pointerType === 'touch' ? this.touchSensitivity : this.sensitivity;
      this.dragAccumulator += delta;
      
      // Change frame when accumulated enough
      if (Math.abs(this.dragAccumulator) >= sens) {
        const frameDelta = Math.floor(this.dragAccumulator / sens);
        this.showFrame(this.currentFrame + frameDelta);
        this.dragAccumulator = this.dragAccumulator % sens;
      }
    }
    
    handlePointerUp(event) {
      if (!this.isDragging) return;
      
      this.isDragging = false;
      this.style.cursor = 'grab';
      
      if (event.pointerId !== undefined) {
        this.releasePointerCapture(event.pointerId);
      }
      
      document.removeEventListener('pointermove', this.onPointerMove);
      document.removeEventListener('pointerup', this.onPointerUp);
    }
  }
  
  customElements.define('sprite-360-viewer', Sprite360Viewer);
}
